"use client";

import { useCallback, useEffect, useState } from "react";

/**
 * The `beforeinstallprompt` event, which the DOM lib does not type. Only the
 * two members the install action uses are declared.
 */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isBeforeInstallPromptEvent(event: Event): event is BeforeInstallPromptEvent {
  return typeof (event as { prompt?: unknown }).prompt === "function";
}

/**
 * Installability for Progsity IDE (`PG-PWA01`, ADR-0052), kept unobtrusive:
 * the browser's own prompt is captured and held, and the page shows an
 * install item only while it holds one. No banner, no toast, nothing on
 * browsers that never fire the event (Safari, Firefox desktop), and nothing
 * once the app is installed, because the event stops firing then.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<boolean> } {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      if (!isBeforeInstallPromptEvent(event)) return;
      event.preventDefault();
      setDeferred(event);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!deferred) return false;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      return choice.outcome === "accepted";
    } catch {
      return false;
    } finally {
      // A held prompt can be used once; the browser fires a new event later
      // if the person dismissed this one.
      setDeferred(null);
    }
  }, [deferred]);

  return { canInstall: deferred !== null, install };
}
